import { filter } from '@ember/object/computed';
import { computed } from '@ember/object';
import { hasMany, belongsTo, attr } from '@ember-data/model';
import ShModel from './sh-model';

const TIERS = {
  FREE: 1,
  PRO: 3,
  ENTERPRISE: 4,
  BUSINESS: 6,
  BLACK: 7,
};

const TIER_NAMES = {
  [TIERS.FREE]: 'Create',
  [TIERS.PRO]: 'Pro',
  [TIERS.ENTERPRISE]: 'Enterprise',
  [TIERS.BUSINESS]: 'Business',
  [TIERS.BLACK]: 'Glimmer Black',
};

const VERSION_TIERS = {
  1: [
    TIERS.FREE,
    TIERS.PRO,
    TIERS.BUSINESS,
    TIERS.ENTERPRISE,
    TIERS.BLACK,
  ],
  2: [
    TIERS.FREE,
    TIERS.PRO,
    TIERS.BUSINESS,
    TIERS.ENTERPRISE,
    TIERS.BLACK,
  ],
};

const TIER_VERSIONS = {
  ONE: 1,
  TWO: 2,
};

const TIER_VERSION_NAMES = {
  [TIER_VERSIONS.ONE]: '1 (legacy)',
  [TIER_VERSIONS.TWO]: '2',
};

const TYPES = {
  ACTIVE: 'active',
  SHELL: 'shell',
};

const TYPE_NAMES = {
  [TYPES.ACTIVE]: 'Active',
  [TYPES.SHELL]: 'Shell',
};

const FEATURES = {
  ADVANCES: 'advances',
  ALWAYS_COVER_FEES: 'always_cover_fees',
  BRANDS: 'brands',
  CAN_SEARCH: 'can_search',
  COMPANY_TEAMS: 'uses_company_teams',
  COST_CODES: 'uses_cost_codes',
  CREDITS: 'credits',
  INVITE_MAX: 'invite_max',
  LOCKOUT_OVERRIDE: 'lockout_override',
  PUBLIC_PROJECTS: 'public_projects',
  REPORTS: 'reports',
  REQUIRES_COST_CODES: 'requires_cost_codes',
  FEATURE_REQUIRES_POSTING_APPROVAL: 'requires_posting_approval',
  DIRECT_MESSAGE: 'direct_message',
  EXPENSES: 'expenses',
};

export const COMPANY = {
  FEATURES,
  TIERS,
  TIER_NAMES,
  TIER_VERSIONS,
  TIER_VERSION_NAMES,
  TYPES,
  TYPE_NAMES,
  VERSION_TIERS,
};

const Company = ShModel.extend({
  approved: attr('boolean'),
  name: attr('string'),
  description: attr('string'),
  website: attr('string'),
  slug: attr('string'),
  type: attr('string'),
  entityId: attr('number'),
  features: attr(),
  tier: attr('number'),
  tierVersion: attr('number'),
  isNeedsCreditCard: attr('boolean'),
  groupsMeta: attr(),
  memberCount: attr('number'),
  hasAssignments: attr('boolean'),
  usesCredits: attr('boolean'),
  networkSize: attr('number'),

  /**
   * Special relationship should be loaded through the company-commands service.
   */
  allEmployees: hasMany('user', { async: false }),
  assets: hasMany('asset', { async: false }),
  blockUserEvents: hasMany('blockUserEvent', { async: false }),
  blockedUsers: hasMany('user', { async: false, inverse: null }),
  brands: hasMany('brand', { async: false }),
  cards: hasMany('card', { async: false }),
  companyBilling: belongsTo('companyBilling', { async: false }),
  companyInvoiceStats: belongsTo('companyInvoiceStat', { async: false }),
  companyMemberships: hasMany('companyMembership', { async: false }),
  companyMembershipStats: belongsTo('companyMembershipStat', { async: false }),
  companyPostingStats: belongsTo('companyPostingStat', { async: false }),
  companyProjectStats: belongsTo('companyProjectStat', { async: false }),
  companyLatestReportStats: belongsTo('companyLatestReportStat', { async: false }),
  companyTeams: hasMany('companyTeam', { async: false }),
  costCodes: hasMany('costCode', { async: false }),
  defaultCostCode: belongsTo('costCode', { async: false, inverse: null }),
  domains: hasMany('domain', { async: false }),
  expeditePaymentFee: attr('number'),
  groups: hasMany('companyGroup', { async: false }),
  image: belongsTo('image', { async: false }),
  industries: hasMany('industry', { async: false }),
  invites: hasMany('invite', { async: false }),
  legals: hasMany('legal', { async: false }),
  location: belongsTo('location', { async: false }),
  approvedEmployees: hasMany('user', { async: false }),
  notes: hasMany('note', { async: false }),
  shInvoices: hasMany('shInvoice', { async: false }),
  // companySellerConnections: hasMany('companySellerConnection', { async: false }),
  statistics: belongsTo('companyStatistic', { async: false }),
  storyCodes: hasMany('storyCode', { async: false }),
  subscription: belongsTo('subscription', { async: false }),

  alwaysCoversCreatorFees: computed('features', function() {
    const { features } = this;
    return Boolean(features?.[COMPANY.FEATURES.ALWAYS_COVER_FEES]);
  }),

  companyAdmins: filter(
    'companyMemberships.@each.admin',
    function (companyMembership) {
      return !!companyMembership.admin;
    }
  ),

  companyEditors: filter(
    'companyMemberships.@each.editor',
    function (companyMembership) {
      return !!companyMembership.editor;
    }
  ),

  /** @type {String} */
  groupId: computed('groupsMeta.@each.is_primary', function () {
    const groupsMeta = this.get('groupsMeta');

    return Object.keys(groupsMeta).find((groupId) => groupsMeta[groupId].is_primary) || '';
  }),

  /** @type {Boolean} */
  sharesRates: computed('groupsMeta.@each.shares_rates', 'groupId', function () {
    const group = this.get('groupsMeta')[this.get('groupId')];

    return group ? !!group.shares_rates : false;
  }),

  /**
   * @type {Boolean}    True if more than one brand exists (excluding default brands).
   */
  usesCompanyBrands: computed('brands', function() {
    return this.brands?.filter((brand) => brand.default !== true).length > 0;
  }),

  /**
   * @type {Boolean}    True if can use cost codes as defined by the company features.
   */
  usesCostCodes: computed('features', function() {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.COST_CODES];
  }),

  /**
   * @type {Boolean}
   */
  canAdvance: computed('features', function() {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.ADVANCES];
  }),

  /**
   * @type {Boolean}    True if canSearch as defined by the company features.
   */
  canSearch: computed('features', function() {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.CAN_SEARCH];
  }),

  /**
   * @type {Boolean}    True when this company can show public projects.
   */
  canShowPublicProjects: computed('features', function() {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.PUBLIC_PROJECTS];
  }),

  /**
   * @type {Boolean}    True when this company has company teams enabled.
   */
  canUseCompanyTeams: computed('features', function () {
    const features = this.features;
    return features && !!features[COMPANY.FEATURES.COMPANY_TEAMS];
  }),

  canUseCredits: computed('features', function () {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.CREDITS];
  }),

  /**
   * @type {Boolean}    True if user can see reporting as defined by the company features.
   */
  canUseReports: computed('features', function() {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.REPORTS];
  }),

  /**
   * @type {Boolean}    True if can send direct messages as defined by the company features.
   */
  canDirectMessage: computed('features', function() {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.DIRECT_MESSAGE];
  }),

  /**
   * @type {Boolean}    True if the user must set a cost code when completing projects
   */
  doesRequireCostCodes: computed('features', function() {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.REQUIRES_COST_CODES];
  }),

  /**
   * @type {Boolean} Whether the company can enable expenses.
   */
  canEnableExpenses: computed('features', function() {
    const features = this.get('features');
    return features && !!features[COMPANY.FEATURES.EXPENSES];
  }),

  /**
   * @type {number} Soft limit on the number of invites that can be made
   */
  inviteMax: computed('features', function () {
    const { features } = this;
    return features?.[COMPANY.FEATURES.INVITE_MAX];
  }),

  /**
   * Relative path to the user's profile
   * @type {String}
   */
  path: computed(
    'slug',
    'id',
    function () {
      const slug = this.get('slug');
      return slug ? `/company/${slug}` : `/company/${this.get('id')}`;
    }
  ),

  /**
   * @type {Boolean} Whether current company is paying for subscription
   */
  usesPaidTier: computed('tier', function () {
    const { tier } = this;
    return tier !== COMPANY.TIERS.FREE;
  }),

  /**
   * Check to see if a given user id is blocked by this company.
   *
   * @param {User|string|number} user
   * @return {Boolean}
   */
  isUserBlockedByCompany(user) {
    let usersBlockedList = this.get('blockedUsers'),
    userId = user.get ? user.get('id') : String(user);

    return usersBlockedList.find((blockedUser) => blockedUser.get('id') === userId);
  }
});

export default Company;
